import { Controller } from "@hotwired/stimulus";
import { toast } from "../services/toast";

// Controller para el modal de pago
// Maneja el cálculo de cambio, múltiples formas de pago y detalles de tarjeta via popup
export default class extends Controller {
  static targets = [
    "paymentType",
    "paymentAmount",
    "paymentType2",
    "paymentAmount2",
    "paymentMethod2",
    "addPaymentBtn",
    "totalReceived",
    "changeAmount",
    "printTicket",
    "completeBtn",
    // Card badge indicators
    "cardBadge1",
    "cardBadgeText1",
    "cardBadge2",
    "cardBadgeText2",
    // Card dialog
    "cardDialog",
    "cardDialogIndex",
    "dialogBankSelect",
    "dialogAccountSelect",
    "dialogCardLastFour",
    "dialogAuthCode",
    // Hidden fields for card data (payment 1)
    "bankAccountId1",
    "bankName1",
    "accountName1",
    "cardLastFour1",
    "authCode1",
    // Hidden fields for card data (payment 2)
    "bankAccountId2",
    "bankName2",
    "accountName2",
    "cardLastFour2",
    "authCode2",
    // Refund mode targets
    "refundSection",
    "refundMethod",
    "refundAmount",
  ];

  static values = {
    total: Number,
    refundMode: Boolean,
    refundAmount: Number,
  };

  connect() {
    this.calculateChange();
  }

  // ==================
  // FORMAS DE PAGO
  // ==================

  handlePaymentTypeChange(event) {
    const isCard =
      event.target.value === "debit_card" ||
      event.target.value === "credit_card";

    if (isCard) {
      // Abrir el popup de datos de tarjeta automáticamente
      this.openCardModal({ params: { index: "1" } });
    } else {
      // Limpiar datos de tarjeta si cambia a efectivo
      this._clearCardData("1");
      if (this.hasCardBadge1Target) {
        this.cardBadge1Target.style.display = "none";
      }
    }

    this.calculateChange();
  }

  handlePaymentType2Change(event) {
    const isCard =
      event.target.value === "debit_card" ||
      event.target.value === "credit_card";

    if (isCard) {
      this.openCardModal({ params: { index: "2" } });
    } else {
      this._clearCardData("2");
      if (this.hasCardBadge2Target) {
        this.cardBadge2Target.style.display = "none";
      }
    }

    this.calculateChange();
  }

  addPaymentMethod() {
    if (this.hasPaymentMethod2Target) {
      this.paymentMethod2Target.style.display = "block";
    }
    if (this.hasAddPaymentBtnTarget) {
      this.addPaymentBtnTarget.style.display = "none";
    }

    // Calcular monto restante para el segundo pago
    const amount1 = parseFloat(this.paymentAmountTarget.value) || 0;
    const remaining = Math.max(0, this.totalValue - amount1);

    if (this.hasPaymentAmount2Target) {
      this.paymentAmount2Target.value = remaining.toFixed(2);
    }

    this.calculateChange();
  }

  removePaymentMethod2() {
    if (this.hasPaymentMethod2Target) {
      this.paymentMethod2Target.style.display = "none";
    }
    if (this.hasAddPaymentBtnTarget) {
      this.addPaymentBtnTarget.style.display = "inline-flex";
    }
    if (this.hasPaymentAmount2Target) {
      this.paymentAmount2Target.value = "0";
    }
    if (this.hasPaymentType2Target) {
      this.paymentType2Target.value = "cash";
    }

    // Limpiar datos de tarjeta del pago 2
    this._clearCardData("2");
    if (this.hasCardBadge2Target) {
      this.cardBadge2Target.style.display = "none";
    }

    this.calculateChange();
  }

  // ==================
  // CÁLCULO DE CAMBIO
  // ==================

  calculateChange() {
    const amount1 = this.hasPaymentAmountTarget
      ? parseFloat(this.paymentAmountTarget.value) || 0
      : 0;
    const amount2 = this.hasPaymentAmount2Target
      ? parseFloat(this.paymentAmount2Target.value) || 0
      : 0;

    const totalReceived = amount1 + amount2;
    const change = totalReceived - this.totalValue;

    // Actualizar displays
    if (this.hasTotalReceivedTarget) {
      this.totalReceivedTarget.textContent = this.formatCurrency(totalReceived);
    }

    if (this.hasChangeAmountTarget) {
      this.changeAmountTarget.textContent = this.formatCurrency(
        Math.max(0, change),
      );
      this.changeAmountTarget.classList.toggle(
        "change-amount--negative",
        change < 0,
      );
    }

    // Habilitar/deshabilitar botón de completar
    if (this.hasCompleteBtnTarget) {
      const isValid =
        totalReceived >= this.totalValue && this.validatePayments();
      this.completeBtnTarget.disabled = !isValid;
    }
  }

  validatePayments() {
    // Validar que si es tarjeta, tenga los datos de tarjeta capturados
    if (this.hasPaymentTypeTarget) {
      const isCard =
        this.paymentTypeTarget.value === "debit_card" ||
        this.paymentTypeTarget.value === "credit_card";
      if (isCard) {
        if (!this._hasCardData("1")) return false;
      }
    }

    // Validar segundo pago si está visible
    if (
      this.hasPaymentMethod2Target &&
      this.paymentMethod2Target.style.display !== "none"
    ) {
      if (this.hasPaymentType2Target) {
        const isCard2 =
          this.paymentType2Target.value === "debit_card" ||
          this.paymentType2Target.value === "credit_card";
        if (isCard2) {
          if (!this._hasCardData("2")) return false;
        }
      }
    }

    return true;
  }

  // ==================
  // DIALOG DE TARJETA
  // ==================

  openCardModal(event) {
    const index = event.params?.index || "1";

    if (!this.hasCardDialogTarget) return;

    // Guardar el índice del pago que estamos editando
    if (this.hasCardDialogIndexTarget) {
      this.cardDialogIndexTarget.value = index;
    }

    // Pre-cargar datos existentes si los hay
    this._prefillDialog(index);

    // Abrir el dialog
    this.cardDialogTarget.showModal();
  }

  async onBankChange(event) {
    const bankId = event.target.value;
    const accountSelect = this.dialogAccountSelectTarget;

    // Limpiar opciones actuales
    accountSelect.innerHTML = '<option value="">Seleccionar cuenta...</option>';

    if (!bankId) {
      accountSelect.disabled = true;
      return;
    }

    try {
      const response = await fetch(
        `/caja/ordenes/bank_accounts_for_bank?bank_id=${bankId}`,
        {
          headers: { Accept: "application/json" },
        },
      );

      if (response.ok) {
        const accounts = await response.json();

        accounts.forEach((account) => {
          const option = document.createElement("option");
          option.value = account.id;
          option.textContent =
            account.name + (account.number ? ` (${account.number})` : "");
          option.dataset.accountName = account.name;
          accountSelect.appendChild(option);
        });

        accountSelect.disabled = false;
      }
    } catch (error) {
      console.error("Error cargando cuentas bancarias:", error);
    }
  }

  cancelCardDialog() {
    if (!this.hasCardDialogTarget) return;

    const index = this.cardDialogIndexTarget.value;

    // Si no hay datos previos guardados, revertir el select de tipo de pago a efectivo
    if (!this._hasCardData(index)) {
      if (index === "1" && this.hasPaymentTypeTarget) {
        this.paymentTypeTarget.value = "cash";
      } else if (index === "2" && this.hasPaymentType2Target) {
        this.paymentType2Target.value = "cash";
      }
    }

    this._resetDialog();
    this.cardDialogTarget.close();
    this.calculateChange();
  }

  acceptCardDialog() {
    const index = this.cardDialogIndexTarget.value;

    // Validar campos requeridos
    const bankId = this.dialogBankSelectTarget.value;
    const accountId = this.dialogAccountSelectTarget.value;
    const cardLastFour = this.dialogCardLastFourTarget.value.trim();
    const authCode = this.dialogAuthCodeTarget.value.trim();

    if (!bankId) {
      toast.warning("Seleccione un banco");
      return;
    }
    if (!accountId) {
      toast.warning("Seleccione una cuenta");
      return;
    }
    if (!cardLastFour || cardLastFour.length !== 4) {
      toast.warning("Ingrese los 4 dígitos de la tarjeta");
      return;
    }
    if (!authCode) {
      toast.warning("Ingrese el número de autorización");
      return;
    }

    // Obtener el texto del banco y la cuenta seleccionados
    const bankName =
      this.dialogBankSelectTarget.options[
        this.dialogBankSelectTarget.selectedIndex
      ].text;
    const accountOption =
      this.dialogAccountSelectTarget.options[
        this.dialogAccountSelectTarget.selectedIndex
      ];
    const accountName = accountOption.dataset.accountName || accountOption.text;

    // Guardar datos en los campos ocultos correspondientes
    this._setCardData(index, {
      bankAccountId: accountId,
      bankName: bankName,
      accountName: accountName,
      cardLastFour: cardLastFour,
      authCode: authCode,
    });

    // Mostrar badge de confirmación
    const badgeTarget = index === "1" ? "cardBadge1" : "cardBadge2";
    const badgeTextTarget = index === "1" ? "cardBadgeText1" : "cardBadgeText2";

    if (this[`has${this._capitalize(badgeTarget)}Target`]) {
      this[`${badgeTarget}Target`].style.display = "flex";
    }
    if (this[`has${this._capitalize(badgeTextTarget)}Target`]) {
      this[`${badgeTextTarget}Target`].textContent =
        `${bankName} •••• ${cardLastFour}`;
    }

    // Cerrar dialog
    this._resetDialog();
    this.cardDialogTarget.close();
    this.calculateChange();
  }

  // ==================
  // PROCESAR PAGO
  // ==================

  async processPayment(event) {
    const url = event.currentTarget.dataset.url;
    const printTicket = this.hasPrintTicketTarget
      ? this.printTicketTarget.checked
      : false;

    // Disable button while processing
    this.completeBtnTarget.disabled = true;
    this.completeBtnTarget.textContent = "Procesando...";

    let body;
    if (this.refundModeValue) {
      // Refund mode: send refund data
      body = JSON.stringify({
        refund_mode: true,
        refund_method: this.hasRefundMethodTarget ? this.refundMethodTarget.value : "cash",
        refund_amount: this.refundAmountValue,
        payments: [],
        print_ticket: printTicket,
      });
    } else {
      const payments = this.collectPayments();
      body = JSON.stringify({
        payments: payments,
        print_ticket: printTicket,
      });
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: body,
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.closeModal();

        // Si debe imprimir, abrir ventana de impresión
        if (printTicket) {
          // TODO: implement ticket printing
        }
      } else {
        const errorHtml = await response.text();
        Turbo.renderStreamMessage(errorHtml);

        // Re-enable button on error
        this.completeBtnTarget.disabled = false;
        const btnLabel = this.refundModeValue ? 'Procesar Devolución' : 'Completar Venta';
        this.completeBtnTarget.innerHTML =
          `<svg class="icon icon--sm"><use href="#check"></use></svg> ${btnLabel}`;
      }
    } catch (error) {
      console.error("Error procesando pago:", error);
      this.completeBtnTarget.disabled = false;
      const btnLabel = this.refundModeValue ? 'Procesar Devolución' : 'Completar Venta';
      this.completeBtnTarget.innerHTML =
        `<svg class="icon icon--sm"><use href="#check"></use></svg> ${btnLabel}`;
    }
  }

  collectPayments() {
    const payments = [];

    // Pago 1
    if (this.hasPaymentTypeTarget && this.hasPaymentAmountTarget) {
      const amount = parseFloat(this.paymentAmountTarget.value) || 0;
      if (amount > 0) {
        const paymentType = this.paymentTypeTarget.value;
        const isCard =
          paymentType === "debit_card" || paymentType === "credit_card";

        const payment = {
          method: paymentType,
          amount: amount,
        };

        if (isCard) {
          payment.bank_account_id = this.hasBankAccountId1Target
            ? this.bankAccountId1Target.value
            : null;
          payment.bank_name = this.hasBankName1Target
            ? this.bankName1Target.value
            : null;
          payment.account_name = this.hasAccountName1Target
            ? this.accountName1Target.value
            : null;
          payment.card_last_four = this.hasCardLastFour1Target
            ? this.cardLastFour1Target.value
            : null;
          payment.authorization_code = this.hasAuthCode1Target
            ? this.authCode1Target.value
            : null;
        }

        payments.push(payment);
      }
    }

    // Pago 2 (si está visible)
    if (
      this.hasPaymentMethod2Target &&
      this.paymentMethod2Target.style.display !== "none"
    ) {
      if (this.hasPaymentType2Target && this.hasPaymentAmount2Target) {
        const amount = parseFloat(this.paymentAmount2Target.value) || 0;
        if (amount > 0) {
          const paymentType = this.paymentType2Target.value;
          const isCard =
            paymentType === "debit_card" || paymentType === "credit_card";

          const payment = {
            method: paymentType,
            amount: amount,
          };

          if (isCard) {
            payment.bank_account_id = this.hasBankAccountId2Target
              ? this.bankAccountId2Target.value
              : null;
            payment.bank_name = this.hasBankName2Target
              ? this.bankName2Target.value
              : null;
            payment.account_name = this.hasAccountName2Target
              ? this.accountName2Target.value
              : null;
            payment.card_last_four = this.hasCardLastFour2Target
              ? this.cardLastFour2Target.value
              : null;
            payment.authorization_code = this.hasAuthCode2Target
              ? this.authCode2Target.value
              : null;
          }

          payments.push(payment);
        }
      }
    }

    return payments;
  }

  // ==================
  // MODAL
  // ==================

  closeModal() {
    const dialog = this.element.closest("dialog");
    if (dialog) {
      dialog.close();
    }

    // Limpiar container
    const container = document.getElementById("pos-modal-container");
    if (container) {
      container.innerHTML = "";
    }
  }

  // ==================
  // HELPERS PRIVADOS
  // ==================

  _clearCardData(index) {
    const fields = [
      "bankAccountId",
      "bankName",
      "accountName",
      "cardLastFour",
      "authCode",
    ];
    fields.forEach((field) => {
      const targetName = `${field}${index}`;
      const hasMethod = `has${this._capitalize(targetName)}Target`;
      if (this[hasMethod]) {
        this[`${targetName}Target`].value = "";
      }
    });
  }

  _hasCardData(index) {
    const bankAccountIdTarget = `bankAccountId${index}`;
    const cardLastFourTarget = `cardLastFour${index}`;
    const authCodeTarget = `authCode${index}`;

    const hasBankAccount =
      this[`has${this._capitalize(bankAccountIdTarget)}Target`] &&
      this[`${bankAccountIdTarget}Target`].value.trim() !== "";
    const hasCard =
      this[`has${this._capitalize(cardLastFourTarget)}Target`] &&
      this[`${cardLastFourTarget}Target`].value.trim() !== "";
    const hasAuth =
      this[`has${this._capitalize(authCodeTarget)}Target`] &&
      this[`${authCodeTarget}Target`].value.trim() !== "";

    return hasBankAccount && hasCard && hasAuth;
  }

  _setCardData(index, data) {
    const mapping = {
      bankAccountId: data.bankAccountId,
      bankName: data.bankName,
      accountName: data.accountName,
      cardLastFour: data.cardLastFour,
      authCode: data.authCode,
    };

    Object.entries(mapping).forEach(([field, value]) => {
      const targetName = `${field}${index}`;
      const hasMethod = `has${this._capitalize(targetName)}Target`;
      if (this[hasMethod]) {
        this[`${targetName}Target`].value = value || "";
      }
    });
  }

  _prefillDialog(index) {
    // Pre-llenar el dialog con datos existentes del pago indicado
    const bankAccountIdTarget = `bankAccountId${index}`;
    const cardLastFourTargetName = `cardLastFour${index}`;
    const authCodeTargetName = `authCode${index}`;

    const existingBankAccountId = this[
      `has${this._capitalize(bankAccountIdTarget)}Target`
    ]
      ? this[`${bankAccountIdTarget}Target`].value
      : "";
    const existingCardLastFour = this[
      `has${this._capitalize(cardLastFourTargetName)}Target`
    ]
      ? this[`${cardLastFourTargetName}Target`].value
      : "";
    const existingAuthCode = this[
      `has${this._capitalize(authCodeTargetName)}Target`
    ]
      ? this[`${authCodeTargetName}Target`].value
      : "";

    // Reset dialog fields
    this._resetDialog();

    // Pre-fill if we have existing data
    if (existingCardLastFour) {
      this.dialogCardLastFourTarget.value = existingCardLastFour;
    }
    if (existingAuthCode) {
      this.dialogAuthCodeTarget.value = existingAuthCode;
    }

    // Note: bank and account selects need async loading, so we don't pre-fill those
    // The user will need to re-select if editing
  }

  _resetDialog() {
    if (this.hasDialogBankSelectTarget) {
      this.dialogBankSelectTarget.value = "";
    }
    if (this.hasDialogAccountSelectTarget) {
      this.dialogAccountSelectTarget.innerHTML =
        '<option value="">Seleccionar cuenta...</option>';
      this.dialogAccountSelectTarget.disabled = true;
    }
    if (this.hasDialogCardLastFourTarget) {
      this.dialogCardLastFourTarget.value = "";
    }
    if (this.hasDialogAuthCodeTarget) {
      this.dialogAuthCodeTarget.value = "";
    }
  }

  _capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  }
}
